import { useState } from 'react';
import { DataLoadError } from '@/components/DataLoadError';
import { createFileRoute } from '@tanstack/react-router';
import { CANONICAL_TABLES } from '@/data/mock';
import { useCanonicalTables } from '@/lib/use-admin-data';
import type { CanonicalTable, CanonicalField } from '@/types/index';
import { cn } from '@/lib/utils';
import { Table, TreeStructure, Wrench, Lightbulb, PencilSimple, CheckCircle } from '@phosphor-icons/react';

/* ------------------------------------------------------------------ */
/*  Source-platform badge config                                       */
/* ------------------------------------------------------------------ */
const SOURCE_COLORS: Record<string, { bg: string; text: string; abbr: string }> = {
  chargebee:    { bg: 'bg-blue-600',   text: 'text-white', abbr: 'CB' },
  recurly:      { bg: 'bg-purple-600', text: 'text-white', abbr: 'RC' },
  braintree:    { bg: 'bg-teal-600',   text: 'text-white', abbr: 'BT' },
  paypal:       { bg: 'bg-sky-600',    text: 'text-white', abbr: 'PP' },
  checkout_com: { bg: 'bg-slate-500',  text: 'text-white', abbr: 'CO' },
};

function SourceCircle({ platform }: { platform: string }) {
  const c = SOURCE_COLORS[platform] ?? { bg: 'bg-gray-400', text: 'text-white', abbr: platform.slice(0, 2).toUpperCase() };
  return (
    <span
      title={platform}
      className={cn(
        'inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold leading-none -ml-1.5 first:ml-0 ring-2 ring-white',
        c.bg,
        c.text,
      )}
    >
      {c.abbr}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Type badge                                                         */
/* ------------------------------------------------------------------ */
function TypeBadge({ type }: { type: string }) {
  const colorMap: Record<string, string> = {
    STRING:    'bg-violet-100 text-violet-700',
    INTEGER:   'bg-sky-100 text-sky-700',
    FLOAT:     'bg-cyan-100 text-cyan-700',
    BOOLEAN:   'bg-amber-100 text-amber-700',
    TIMESTAMP: 'bg-rose-100 text-rose-700',
    DATE:      'bg-blue-100 text-blue-700',
    JSON:      'bg-emerald-100 text-emerald-700',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded text-xs font-semibold', colorMap[type] ?? 'bg-slate-100 text-slate-600')}>
      {type}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Tabs                                                               */
/* ------------------------------------------------------------------ */
const TABS = ['Field Definitions', 'Source Mappings', 'Usage Stats', 'Access Logs'] as const;

/* ------------------------------------------------------------------ */
/*  Page component                                                     */
/* ------------------------------------------------------------------ */
function CanonicalSchemaPage() {
  const { data: tables = [], isError: tablesError } = useCanonicalTables();
  const [selectedTableId, setSelectedTableId] = useState('billing_customer');
  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]>('Field Definitions');

  const selectedTable: CanonicalTable | undefined = tables.find((t) => t.id === selectedTableId);

  return (
    <div className="flex h-full min-h-0">
      {tablesError && <DataLoadError what="canonical tables" />}
      {/* ---- Left Sidebar ---- */}
      <aside className="w-72 flex-shrink-0 bg-slate-900 text-white overflow-y-auto border-r border-slate-800">
        <div className="px-5 pt-6 pb-3">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Canonical Schemas</h3>
        </div>
        <nav className="px-3 pb-6 space-y-0.5">
          {tables.map((table) => {
            const isActive = table.id === selectedTableId;
            return (
              <button
                key={table.id}
                onClick={() => {
                  setSelectedTableId(table.id);
                  setActiveTab('Field Definitions');
                }}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left',
                  isActive
                    ? 'bg-slate-700 text-white border border-slate-600'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white border border-transparent',
                )}
              >
                <Table size={18} weight="regular" />
                <span className="truncate">{table.id}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ---- Main Content ---- */}
      <main className="flex-1 overflow-y-auto bg-[#f8f6f6] p-8">
        {selectedTable ? (
          <>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-3xl font-black text-slate-900">{selectedTable.id}</h2>
                <p className="text-sm text-slate-500 mt-1 max-w-2xl">{selectedTable.description}</p>
              </div>
              <div className="flex items-center gap-3">
                <button className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-white transition-colors">
                  <PencilSimple size={16} weight="regular" className="inline-block align-middle mr-1" />
                  Edit Schema
                </button>
                <span className="px-3 py-1.5 rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                  Version {selectedTable.version}
                </span>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-slate-200 mb-6">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'px-4 py-2.5 text-sm font-semibold transition-colors -mb-px',
                    activeTab === tab
                      ? 'border-b-2 border-slate-900 text-slate-900'
                      : 'text-slate-500 hover:text-slate-700',
                  )}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Field Definitions Table */}
            {activeTab === 'Field Definitions' && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                {selectedTable.fields.length === 0 ? (
                  <div className="px-8 py-16 text-center text-slate-400 text-sm">
                    <TreeStructure size={32} weight="regular" className="mb-2 mx-auto" />
                    No fields defined yet. Click "Edit Schema" to add fields.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                        <th className="px-5 py-3">Field Name</th>
                        <th className="px-5 py-3">Type</th>
                        <th className="px-5 py-3 text-center">Req</th>
                        <th className="px-5 py-3">Source Mappings</th>
                        <th className="px-5 py-3">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedTable.fields.map((field: CanonicalField) => (
                        <tr key={field.name} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-5 py-3 font-mono text-slate-900 font-semibold text-[13px]">
                            {field.name}
                          </td>
                          <td className="px-5 py-3">
                            <TypeBadge type={field.type} />
                          </td>
                          <td className="px-5 py-3 text-center">
                            {field.required ? (
                              <CheckCircle size={18} weight="regular" className="text-emerald-500 inline-block" />
                            ) : (
                              <span className="text-slate-300">&mdash;</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center">
                              {field.sourceMappings.map((platform) => (
                                <SourceCircle key={platform} platform={platform} />
                              ))}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-slate-500 max-w-xs truncate" title={field.description}>
                            {field.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Placeholder for other tabs */}
            {activeTab !== 'Field Definitions' && (
              <div className="bg-white rounded-xl border border-slate-200 px-8 py-16 text-center text-slate-400 text-sm shadow-sm">
                <Wrench size={32} weight="regular" className="mb-2 mx-auto" />
                {activeTab} coming soon.
              </div>
            )}
          </>
        ) : (
          <div className="text-slate-400 text-sm pt-20 text-center">Select a table from the sidebar.</div>
        )}
      </main>

      {/* ---- Right Sidebar (Stats Panel) ---- */}
      <aside className="w-80 flex-shrink-0 bg-white border-l border-slate-200 overflow-y-auto p-6 space-y-6">
        <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Field Usage Stats</h3>

        {/* Populated % bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-semibold text-slate-700">Populated</span>
            <span className="text-sm font-bold text-slate-900 font-mono tabular-nums">92.4%</span>
          </div>
          <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-slate-900" style={{ width: '92.4%' }} />
          </div>
        </div>

        {/* 2x2 stat grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[11px] font-semibold uppercase text-slate-400">Null Rate</p>
            <p className="text-xl font-black text-slate-800 mt-0.5">7.6%</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-[11px] font-semibold uppercase text-slate-400">Mismatch</p>
            <p className="text-xl font-black text-slate-800 mt-0.5">1.2%</p>
          </div>
        </div>

        {/* Health by Tenant */}
        <div>
          <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Health by Tenant</h4>
          <ul className="space-y-2.5">
            {[
              { name: 'PeopleFinders', pct: 94.1, color: 'text-emerald-600 bg-emerald-50' },
              { name: 'Find A Neighborhood', pct: 89.7, color: 'text-emerald-600 bg-emerald-50' },
              { name: 'Kombatix', pct: 78.3, color: 'text-amber-600 bg-amber-50' },
            ].map((t) => (
              <li key={t.name} className="flex items-center justify-between">
                <span className="text-sm text-slate-700 font-medium">{t.name}</span>
                <span className={cn('text-xs font-bold px-2 py-0.5 rounded-full', t.color)}>
                  {t.pct}%
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Registry Insight */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb size={18} weight="regular" className="text-blue-700" />
            <h4 className="text-xs font-bold uppercase tracking-widest text-blue-700">Registry Insight</h4>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            <span className="font-semibold text-slate-800">gateway_customer_id</span> is only mapped from Chargebee.
            Consider adding Braintree and Checkout.com source mappings to improve match rate by ~12%.
          </p>
        </div>
      </aside>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/schema/canonical')({
  component: CanonicalSchemaPage,
});
